import { useData, useDispatch } from "../../../store/DataProvider";
import { useEffect, useState } from "react";
import { handleApiData, handleLocalData } from "../../../store/actions";
import {
    businessDate,
    businessDay,
    asDayString,
    asTimeString,
} from "../../../services/utility";
import DatePicker from "react-datepicker";
import DatepickerInput from "../../admin/DatepickerInput";

const SubmitRow = () => {
    const dispatch = useDispatch();
    const data = useData();
    const { paymentType, currentSales, staff, selectedStaff, shopOptions } =
        data;

    /* the salon's day, not the calendar one -- before 6am this is yesterday */
    const today = businessDate().toISOString();
    const [dateOfSale, setDateOfSale] = useState(today);
    /* Null means "wherever this stylist normally works". Only set when the
       shop has been changed by hand, for a shift covered at another shop. */
    const [shopOverride, setShopOverride] = useState(null);

    const staffShopId = staff.reduce(
        (acc, { _id, shopId }) => (_id === selectedStaff ? shopId : acc),
        null,
    );
    const shopId = shopOverride || staffShopId;

    /* A different stylist means a different default, so an override made for
       the last one is dropped rather than carried silently into this sale. */
    useEffect(() => {
        setShopOverride(null);
    }, [selectedStaff]);

    if (currentSales.length === 0) {
        return null;
    }

    const onClickSubmit = e => {
        e.preventDefault();
        if (paymentType) {
            const items = currentSales.reduce((acc, { serviceId, price }) => {
                acc.push({ serviceId, price });
                return acc;
            }, []);
            const commission = staff.reduce((acc, { _id, commission }) => {
                if (_id === selectedStaff) {
                    acc = commission;
                }
                return acc;
            }, 0);
            const sale = {
                staffId: selectedStaff,
                /* where it happened: this stylist's shop unless it was
                   changed on the row below */
                shopId,
                total,
                commission,
                paymentType,
                items,
                dateOfSale,
                /* the day and the clock reading as they stand on screen, so
                   the server files the sale under them rather than working
                   them out again in its own timezone */
                day: asDayString(dateOfSale),
                time: asTimeString(),
                /* which day the totals coming back should cover: today, even
                   if this sale has been dated to some other day */
                todayIs: businessDay(),
            };
            handleApiData(sale, dispatch, "submitSale");
        } else {
            handleLocalData(
                { type: "alertPaymentType", payload: true },
                dispatch,
            );
        }
    };

    const total = currentSales.reduce((acc, { price }) => {
        acc += price;
        return acc;
    }, 0);

    return (
        <>
            <div
                style={{
                    height: "20px",
                    borderBottom: "3px dotted #a802a0",
                    marginLeft: "25px",
                    marginRight: "25px",
                }}
            />
            <div className="price-row submit-price-row">
                <input
                    className="price-input submit-row"
                    type="text"
                    value={total}
                    disabled
                />
                {/* No "Total" label. It sat beside the only figure on the row,
                    directly under the sale it is the sum of, and the 43px it
                    took was the difference between this row fitting across an
                    iPad in landscape and wrapping Enter onto a second line. */}
                <div
                    className="submit-switch-date"
                    style={{ paddingRight: "7px" }}
                >
                    <DatePicker
                        selected={dateOfSale}
                        onChange={date => setDateOfSale(date)}
                        dateFormat="YYYY-MM-dd"
                        placeholderText="Select date"
                        customInput={
                            <DatepickerInput className="datepicker-input" />
                        }
                        wrapperClassName="datepicker-wrapper"
                    />
                </div>

                {/* Shown, not just recorded. The shop is right nearly every
                    time, being this stylist's own, so it needs no attention --
                    but a wrong one leaves no trace afterwards, which is why it
                    is on screen at the moment of committing rather than
                    inferred silently. A plain select: it is narrow, and on iOS
                    it opens the native picker, which is a faster way through
                    three shops than anything drawn here. */}
                <div className="submit-switch-shop">
                    <select
                        className="shop-select"
                        aria-label="Shop"
                        value={shopId || ""}
                        onChange={e => setShopOverride(e.target.value)}
                    >
                        {shopId ? null : <option value="">Shop?</option>}
                        {(shopOptions || []).map(({ value, label }) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    type="button"
                    className="submit-sale"
                    onClick={onClickSubmit}
                >
                    Enter
                </button>
            </div>
        </>
    );
};

export default SubmitRow;
